import React, { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import Button from "components/Button";

import { ReactComponent as CloseIcon } from "assets/images/icon_close_modal.svg";

import {
  SubscriptionCancellationReasonEnum,
  SubscriptionResponseDto,
} from "api";
import { useAppDispatch } from "store/hooks";
import {
  cancelSubscription,
  getSubscription,
} from "store/features/subscriptionSlice";

import Step1 from "./Step1";
import Step2 from "./Step2";

import { FormValues, validationSchema } from "./Step2/validation";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

import { styles } from "./styles";

export enum CancelSubscriptionSteps {
  Step1 = "Step1",
  Step2 = "Step2",
}

interface CancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: SubscriptionResponseDto;
}

const CancelModal: React.FC<CancelModalProps> = (props) => {
  const dispatch = useAppDispatch();
  const { isOpen, subscription, onClose } = props;

  const [step, setStep] = useState<CancelSubscriptionSteps>(
    CancelSubscriptionSteps.Step1
  );
  const [loading, setLoading] = useState(false);

  const methods = useForm<FormValues>({
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
    defaultValues: {
      reason: [],
      notes: "",
    },
  });

  const onCloseModal = () => {
    onClose();
    methods.reset();
    setStep(CancelSubscriptionSteps.Step1);
  };

  const onCancelSubscription = async () => {
    if (step === CancelSubscriptionSteps.Step1) {
      setStep(CancelSubscriptionSteps.Step2);
      return;
    }

    methods.handleSubmit(async (data) => {
      analytics().sendConversionEvent(
        AnalyticsEvents.subscription_cancel_subscription_confirm,
        null,
        true
      );
      const { reason, notes } = data;
      setLoading(true);

      await dispatch(
        cancelSubscription({
          reason: SubscriptionCancellationReasonEnum.CancelledByCustomer,
          notes: reason.join(", ") + (notes ? `, ${notes}` : ""),
        })
      ).unwrap();

      dispatch(getSubscription()).unwrap();

      onCloseModal();
      setLoading(false);
    })();
  };

  const renderTitle = () => {
    switch (step) {
      case CancelSubscriptionSteps.Step1:
        return <>Скасувати передплату</>;
      case CancelSubscriptionSteps.Step2:
        return (
          <>
            Нам шкода, що ви вирішили <br />
            скасувати підписку.
          </>
        );
      default:
        return <>Скасувати передплату</>;
    }
  };

  const renderStep = () => {
    switch (step) {
      case CancelSubscriptionSteps.Step1:
        return <Step1 subscription={subscription} />;
      case CancelSubscriptionSteps.Step2:
        return <Step2 methods={methods} />;
      default:
        return <Step1 subscription={subscription} />;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCloseModal}
      slotProps={{ paper: { sx: styles.modal } }}
    >
      <DialogTitle component="div" sx={{ p: 0, mr: 4 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {renderTitle()}
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close-modal"
        onClick={onCloseModal}
        sx={styles.closeButton}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: 0, mr: 2 }}>{renderStep()}</DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 2,
            width: "100%",
          }}
        >
          <Button
            color="secondary"
            variant="contained"
            onClick={onCloseModal}
            disabled={loading}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Скасувати
          </Button>
          <Button
            data-testid="confirm-cancel-subscription-button"
            color="error"
            variant="contained"
            onClick={onCancelSubscription}
            withBorder
            fullWidth
            loading={loading}
          >
            Остаточно скасувати передплату
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CancelModal;
